import { InfoOutlined, OpenInNewOutlined } from '@mui/icons-material';
import {
	Box,
	Card,
	CardContent,
	Divider,
	FormControl,
	FormControlLabel,
	IconButton,
	InputAdornment,
	InputLabel,
	MenuItem,
	Select,
	Switch,
	Tab,
	Tabs,
	TextField,
	Tooltip,
	Typography
} from '@mui/material';
import { generateClient } from 'aws-amplify/api';
import { fetchUserAttributes } from 'aws-amplify/auth';
import { allCountries } from 'country-region-data';
import { Formik } from 'formik';
import moment from 'moment';
import { enqueueSnackbar } from 'notistack';
import phone from 'phone';
import { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ConfirmDialog } from '../../components/confirmDialog/confirmDialog';
import { ActivityLog } from '../../components/contacts/activityLog';
import MetadataDialog from '../../components/contacts/pocMetaDataDialog';
import { PageAppBar } from '../../components/pageAppBar';
import { contactValidation } from '../../components/yupValidations';
import { UserContext } from '../../contexts/UserContext';
import { updateContact } from '../../graphql/mutations';
import { getContact, tenantContact } from '../../graphql/queries';
import { customFieldTypes } from '../../models/customFieldsTypes';
import { cardStyle, tabStyle } from '../../theme';
import { fetchAllPages } from '../../tools/graphqlPaging';
import { REGION } from '../../models/globals';

const countryRegionName = {
	US: {
		regions: 'State',
		zip: 'Zip Code'
	},
	CA: {
		regions: 'Province',
		zip: 'Postal Code'
	},
	AU: {
		regions: 'States/Territories',
		zip: 'Postal Code'
	},
	ZA: {
		regions: 'Provinces',
		zip: 'Postal Code'
	}
}

export function Contact() {
	const navigate = useNavigate();
	const client = generateClient();
	const userContext = useContext(UserContext);
	const [contact, setContact] = useState({
		firstName: '',
		lastName: '',
		phone: '',
		cell: '',
		email: '',
		source: '',
		address1: '',
		address2: '',
		city: '',
		state: '',
		zip: '',
		country: '',
		timeZone: '',
		outboundCallerId: '',
		assignedAgent: '',
		externalId: '',
		customFields: {},
	});
	const { id } = useParams();
	const [currentTab, setCurrentTab] = useState(0);
	const [navigateConfirmOpen, setNavigateConfirmOpen] = useState(false);
	const [loading, setLoading] = useState(false);
	const [currentPOCField, setCurrentPOCField] = useState(null);
	const [pocMetadata, setPocMetadata] = useState([]);
	const [regions, setRegions] = useState([]);

	useEffect(() => {
		async function getData() {
			const promiseArray = [];
			promiseArray.push(getContactData());
			promiseArray.push(getPocMetadata());
			await Promise.all(promiseArray);
		}

		if (userContext.tenantId && id) {
			getData();
		}
	}, [userContext.tenantId, id]);

	const getRegionsForCountry = (countryCode) => {
		if (!countryCode) {
			// Default: US + CA
			const us = allCountries.find(c => c[1] === 'US')?.[2] || [];
			const ca = allCountries.find(c => c[1] === 'CA')?.[2] || [];
			return [...us, ...ca];
		}

		const countryData = allCountries.find(c => c[1] === countryCode);
		return countryData?.[2] || [];
	};

	async function getContactData() {
		const contact = await client.graphql({
			query: getContact,
			variables: { id: id },
		});
		if (contact.data.getContact) {
			const customFields = JSON.parse(contact.data.getContact.customFields);
			contact.data.getContact.customFields = customFields;
			setContact(contact.data.getContact);
			let country = contact.data.getContact.country;
			if (!country && userContext.country) {
				country = userContext.country;
			}
			if (country) {
				const countryData = allCountries.find((c) => c[1] === country);
				if (countryData) {
					setRegions(getRegionsForCountry(country));
				}
			} else {
				switch (REGION) {
					case 'af-south-1':
						setRegions(getRegionsForCountry('ZA'));
						break;
					case 'ap-southeast-2':
						setRegions(getRegionsForCountry('AU'));
						break;
					case 'eu-west-2':
						setRegions(getRegionsForCountry('GB'));
						break;
					default:
						setRegions(getRegionsForCountry());
						break;

				}
			}
		}
	}

	async function getPocMetadata() {
		setPocMetadata(
			await fetchAllPages({
				client,
				query: tenantContact,
				resultKey: 'tenantContact',
				variables: {
					tenant: userContext.tenantId,
					contact: { eq: id },
				},
			}),
		);
	}

	function handleDate(name, values) {
		const dateValue = values.customFields[name];
		if (dateValue && Number(dateValue)) {
			return moment(dateValue, 'x').format('YYYY-MM-DD');
		} else if (dateValue) {
			return dateValue;
		} else {
			return '';
		}
	}

	function formatTimeZoneBounds(timeZoneBounds) {
		if (!timeZoneBounds) return 'Not available';
		const formatOffset = (tz) => {
			if (!tz) return 'N/A';
			const offset = tz.offset;
			const sign = offset >= 0 ? '+' : '';
			const dst = tz.dst ? ' (DST)' : '';
			return `UTC${sign}${offset}${dst}`;
		};
		return `Min: ${formatOffset(timeZoneBounds.min)} | Max: ${formatOffset(timeZoneBounds.max)}`;
	}

	return (
		<Box>
			<Formik
				enableReinitialize
				initialValues={contact}
				validationSchema={contactValidation(userContext.customFields, userContext.country)}
				onSubmit={async (values, formikBag) => {
					try {
						setLoading(true);
						const contact = { ...values };

						if (contact.customFields) {
							for (const key in contact.customFields) {
								const tenantCustomField = userContext.customFields.find((field) => field.name === key);
								if (tenantCustomField) {
									if (tenantCustomField.type === 'Number') {
										contact.customFields[key] = +contact.customFields[key];
									} else if (tenantCustomField.type === 'DateTime') {
										const dateField = contact.customFields[key];
										if (dateField && Number(dateField)) {
											contact.customFields[key] = dateField;
										} else {
											contact.customFields[key] = moment(dateField, 'YYYY-MM-DDTHH:mm').format('x');
										}
									} else if (tenantCustomField.type === 'Boolean') {
										contact.customFields[key] = Boolean(contact.customFields[key]);
									} else if (tenantCustomField.type === customFieldTypes.Phone) {
										contact.customFields[key] = phone(contact.customFields[key], { validateMobilePrefix: false }).phoneNumber;
									}
								}
							}
							contact.customFields = JSON.stringify(contact.customFields);
						}

						for (const key in contact) {
							if (contact[key] === '') {
								contact[key] = null;
							}
						}

						if (contact.phone) {
							contact.phone = phone(contact.phone, { validateMobilePrefix: false }).phoneNumber;
						}
						if (contact.cell) {
							contact.cell = phone(contact.cell, { validateMobilePrefix: false }).phoneNumber;
						}

						contact.updatedBy = (await fetchUserAttributes()).name;
						delete contact.createdAt;
						delete contact.updatedAt;
						delete contact.__typename;
						delete contact.timeZoneBounds.__typename;
						for (const field in contact.timeZoneBounds) {
							delete contact.timeZoneBounds[field].__typename;
						}
						const response = await client.graphql({
							query: updateContact,
							variables: { input: contact },
						});
						const updatedContact = response.data.updateContact;
						if (updatedContact.customFields) {
							updatedContact.customFields = JSON.parse(updatedContact.customFields);
						}

						formikBag.setSubmitting(false);
						setContact(updatedContact);
						formikBag.resetForm(updatedContact);
						enqueueSnackbar('Contact updated successfully', { variant: 'success' });

					} catch (err) {
						console.error(err);
						enqueueSnackbar('Error updating contact', { variant: 'error' });
					}
					setLoading(false);
				}}
			>
				{(props) => (
					<form onSubmit={props.handleSubmit}>
						<PageAppBar
							title={`${contact.firstName} ${contact.lastName}`}
							description={`External ID: ${contact.externalId}`}
							titleAdornment={
								<Tooltip
									title={
										<Box>
											<Typography variant="body2" sx={{ fontWeight: 'bold' }}>Attempts: {contact.attempts ?? 0}</Typography>
											<Typography variant="body2" sx={{ fontWeight: 'bold', mt: 0.5 }}>Timezone Bounds</Typography>
											<Typography variant="body2">{formatTimeZoneBounds(contact.timeZoneBounds)}</Typography>
										</Box>
									}
									arrow
								>
									<IconButton size="small">
										<InfoOutlined fontSize="small" color="primary" />
									</IconButton>
								</Tooltip>
							}
							actionOneText="Save"
							actionOneHandler={props.handleSubmit}
							actionOneDisabled={userContext.securityOptions.contacts < 2 || loading}
							actionTwoText="Close"
							// Go back to where we came from
							actionTwoHandler={() => {
								if (props.dirty) {
									// If the form is dirty, we need to ask the user if they want to leave
									setNavigateConfirmOpen(true);
								} else {
									navigate(-1)
								}
							}}
							actionTwoDisabled={loading}

						/>
						<Card sx={cardStyle} variant="outlined">
							<CardContent>
								<Box display="flex" flexDirection="column" gap={2}>
									<Tabs
										value={currentTab}
										onChange={(event, newValue) => setCurrentTab(newValue)}
										textColor="primary"
										indicatorColor="primary"
									>
										<Tab sx={tabStyle} label="Contact Information" />
										<Tab sx={tabStyle} label="Custom Fields" />
										<Tab sx={tabStyle} label="Activity Log" />
									</Tabs>
									<Divider />
									{currentTab === 0 && (
										<Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
											<TextField
												label="Phone"
												value={props.values.phone ? props.values.phone : ''}
												onChange={props.handleChange}
												error={props.errors?.phone && props.touched?.phone}
												helperText={props.errors?.phone && props.touched?.phone && props.errors.phone}
												name="phone"
												InputProps={{
													endAdornment: <InputAdornment position='end'>
														<IconButton onClick={() => setCurrentPOCField('phone')}>
															<OpenInNewOutlined color="primary" />
														</IconButton>
													</InputAdornment>
												}}
											/>
											<TextField
												label="Cell"
												value={props.values.cell ? props.values.cell : ''}
												onChange={props.handleChange}
												error={props.errors?.cell && props.touched?.cell}
												helperText={props.errors?.cell && props.touched?.cell && props.errors.cell}
												name="cell"
												InputProps={{
													endAdornment: <InputAdornment position='end'>
														<IconButton onClick={() => setCurrentPOCField('cell')}>
															<OpenInNewOutlined color="primary" />
														</IconButton>
													</InputAdornment>
												}}
											/>
											<TextField
												label="Email"
												value={props.values.email ? props.values.email : ''}
												onChange={props.handleChange}
												error={props.errors?.email && props.touched?.email}
												helperText={props.errors?.email && props.touched?.email && props.errors.email}
												name="email"
												InputProps={{
													endAdornment: <InputAdornment position='end'>
														<IconButton onClick={() => setCurrentPOCField('email')}>
															<OpenInNewOutlined color="primary" />
														</IconButton>
													</InputAdornment>
												}}
											/>
											<TextField
												label="Source"
												value={props.values.source ? props.values.source : ''}
												onChange={props.handleChange}
												error={props.errors?.source && props.touched?.source}
												helperText={props.errors?.source && props.touched?.source && props.errors.source}
												name="source"
											/>
											<TextField
												label="Address 1"
												value={props.values.address1 ? props.values.address1 : ''}
												onChange={props.handleChange}
												error={props.errors?.address1 && props.touched?.address1}
												helperText={props.errors?.address1 && props.touched?.address1 && props.errors.address1}
												name='address1'
											/>
											<TextField
												label="Address 2"
												value={props.values.address2 ? props.values.address2 : ''}
												onChange={props.handleChange}
												error={props.errors?.address2 && props.touched?.address2}
												helperText={props.errors?.address2 && props.touched?.address2 && props.errors.address2}
												name='address2'
											/>
											<TextField
												label="City"
												value={props.values.city ? props.values.city : ''}
												onChange={props.handleChange}
												error={props.errors?.city && props.touched?.city}
												helperText={props.errors?.city && props.touched?.city && props.errors.city}
												name="city"
											/>
											{regions.length > 0 ? (
												<FormControl key={`state-${props.values.country}-select`}>
													<InputLabel error={props.errors?.state && props.touched?.state}>
														{countryRegionName[props.values.country]?.regions || 'State / Region'}
													</InputLabel>

													<Select
														name="state"
														label={countryRegionName[props.values.country]?.regions || 'State / Region'}
														value={props.values.state || ''}
														onChange={props.handleChange}
														onBlur={props.handleBlur}
														error={props.errors?.state && props.touched?.state}
													>
														{regions.map(([name, code]) => (
															<MenuItem key={code} value={code}>
																{name}
															</MenuItem>
														))}
													</Select>

													{props.errors?.state && props.touched?.state && (
														<FormHelperText error>{props.errors.state}</FormHelperText>
													)}
												</FormControl>
											) : (
												<TextField
													key={`state-${props.values.country}-text`}
													label={countryRegionName[props.values.country]?.regions || 'State / Region'}
													name="state"
													value={props.values.state || ''}
													onChange={props.handleChange}
													onBlur={props.handleBlur}
													error={props.errors?.state && props.touched?.state}
													helperText={
														props.errors?.state &&
														props.touched?.state &&
														props.errors.state
													}
												/>
											)}
											<TextField
												label={countryRegionName[props.values.country] ? countryRegionName[props.values.country].zip : 'Zip Code'}
												value={props.values.zip ? props.values.zip : ''}
												onChange={props.handleChange}
												name="zip"
												error={props.errors?.zip && props.touched?.zip}
												helperText={props.errors?.zip && props.touched?.zip && props.errors.zip}
											/>
											<FormControl>
												<InputLabel error={props.errors?.country && props.touched?.country}>Country</InputLabel>
												<Select
													name="country"
													label="Country"
													onChange={(e) => {
														const newCountry = e.target.value;

														props.handleChange(e);

														// Reset dependent fields
														props.setFieldValue('state', '');
														props.setFieldValue('zip', '');

														// Update regions
														setRegions(getRegionsForCountry(newCountry));
													}}
													onBlur={props.handleBlur}
													value={props.values.country ? props.values.country : ''}
													error={props.errors?.country && props.touched?.country}
												>
													{allCountries.map(country =>
														<MenuItem key={country[1]} value={country[1]}>{country[0]}</MenuItem>)}
												</Select>
												{props.errors?.country && props.touched?.country &&
													<FormHelperText error>{props.errors.country}</FormHelperText>}
											</FormControl>
											<TextField
												label="Outbound Caller ID"
												value={props.values.outboundCallerId ? props.values.outboundCallerId : ''}
												onChange={props.handleChange}
												name="outboundCallerId"
												error={props.errors?.outboundCallerId && props.touched?.outboundCallerId}
												helperText={props.errors?.outboundCallerId && props.touched?.outboundCallerId && props.errors.outboundCallerId}
											/>
											<TextField
												label="Assigned Agent"
												value={props.values.assignedAgent ? props.values.assignedAgent : ''}
												onChange={props.handleChange}
												name="assignedAgent"
												error={props.errors?.assignedAgent && props.touched?.assignedAgent}
												helperText={props.errors?.assignedAgent && props.touched?.assignedAgent && props.errors.assignedAgent}
											/>
										</Box>
									)}
									{currentTab === 1 && (
										<Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
											{userContext.customFields.map((field) =>
												(function () {
													switch (field.type) {
														case 'Number':
														case 'String':
														case 'Phone':
															return (
																<TextField
																	key={field.name}
																	name={`customFields.${field.name}`}
																	label={field.displayName}
																	value={props.values.customFields && props.values.customFields[field.name] ? props.values.customFields[field.name] : ''}
																	error={props.errors?.customFields && props.errors.customFields[field.name] && props.touched.customFields && props.touched.customFields[field.name]}
																	helperText={props.errors?.customFields && props.errors.customFields[field.name] && props.touched.customFields && props.touched.customFields[field.name] && props.errors.customFields[field.name]}
																	onChange={props.handleChange}
																	// Only show input props if field is POC
																	InputProps={field.isPoc && {
																		endAdornment: <InputAdornment position='end'>
																			<IconButton onClick={() => setCurrentPOCField(field.name)}>
																				<OpenInNewOutlined color="primary" />
																			</IconButton>
																		</InputAdornment>
																	}}
																/>
															);
														case 'Boolean':
															return (
																<FormControlLabel
																	key={field.name}
																	control={
																		<Switch
																			name={`customFields.${field.name}`}
																			checked={props.values.customFields && props.values.customFields[field.name]}
																			onChange={props.handleChange}
																		/>
																	}
																	label={field.displayName}
																/>
															);
														// These are saved as an epoch. We need to convert them to a date string for the user to see
														case 'DateTime':
															return (
																<TextField
																	key={field.name}
																	name={`customFields.${field.name}`}
																	label={field.displayName}
																	value={handleDate(field.name, props.values)}
																	error={props.errors?.customFields && props.errors.customFields[field.name] && props.touched.customFields && props.touched.customFields[field.name]}
																	helperText={props.errors?.customFields && props.errors.customFields[field.name] && props.touched.customFields && props.touched.customFields[field.name] && props.errors.customFields[field.name]}
																	onChange={props.handleChange}
																	type="date"
																/>
															);
														default:
															return null;
													}
												})(),
											)}

										</Box>)}

									{currentTab === 2 && (
										<Box>
											<ActivityLog contactId={id} />
										</Box>
									)}
								</Box>
							</CardContent>
						</Card>
						<ConfirmDialog
							open={navigateConfirmOpen}
							actionTwoHandler={() => setNavigateConfirmOpen(false)}
							actionOneHandler={() => {
								setNavigateConfirmOpen(false);
								navigate(-1);
							}}
							title="Discard Changes"
							description="Are you sure you want to discard your changes?"
							actionOneText="Discard"
							actionTwoText="Cancel"
						/>
						<MetadataDialog
							open={currentPOCField}
							close={() => setCurrentPOCField(null)}
							tenant={userContext.tenantId}
							contactId={id}
							field={currentPOCField}
							metadata={pocMetadata}
						/>
					</form>
				)}
			</Formik>

		</Box>
	);
}
